import { cn } from '@/lib/utils';
import { formatTimeRange } from '@/lib/format';
import { CATEGORY, CLASS_SCOPE_LABEL } from '../constants';
import type { CalendarEventItem } from '../types/calendar';

export interface EventChipProps {
  item: CalendarEventItem;
  onSelect: (item: CalendarEventItem) => void;
  /** `day` fits a month cell; `column` has room for the time as well. */
  layout?: 'day' | 'column';
}

/**
 * One entry, as it appears inside a day.
 *
 * The month grid, the week columns and the day a reader opens from the year
 * grid all draw the same thing, so it is drawn once: the category's colour, the
 * title, and the line underneath that says who it touches (CLAUDE.md §3).
 */
export function EventChip({ item, onSelect, layout = 'day' }: EventChipProps) {
  const style = CATEGORY[item.category];
  const timing = formatTimeRange(item.startTime, item.endTime);

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      title={item.title}
      className={cn(
        'flex w-full cursor-pointer flex-col gap-0.5 rounded-lg border px-2 py-1.5 text-left transition-all hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        style.chip,
      )}
    >
      <span className="flex items-center gap-1.5">
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} aria-hidden />
        <span className="truncate text-[11.5px] font-bold">{item.title}</span>
      </span>
      <span className="truncate pl-3 text-[10.5px] font-semibold opacity-80">
        {CLASS_SCOPE_LABEL[item.classScope]}
      </span>
      {layout === 'column' && (
        <span className="truncate pl-3 font-mono text-[10px] font-semibold opacity-70">
          {timing}
        </span>
      )}
    </button>
  );
}
